import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventStream } from './useEventStream';
import { EDIT_LOCK_HEARTBEAT_MS } from '@erp/shared';
import { ApiError, api, editHolder, postKeepalive } from '../lib/api';
import {
  acquiring, canEdit, decideEvent, failed, free, held, initialLockState, locked, lost, shouldBeat,
  shouldReleaseLate, type LockState
} from '../lib/edit-lock-state';

/**
 * Bloqueo de edición de un registro.
 *
 * Pide el bloqueo al abrir, lo renueva **solo mientras hay actividad** y lo suelta al cerrar. La
 * diferencia importa: si latiera siempre, una pestaña olvidada tendría el registro secuestrado toda
 * la noche; así el bloqueo caduca a los 15 minutos sin tocar nada, que es lo acordado.
 */

// El ritmo y el margen de aviso viven en `@erp/shared` para que servidor y web no se separen.
const HEARTBEAT_MS = EDIT_LOCK_HEARTBEAT_MS;

interface Owner {
  lockedBy: { fullName: string };
  terminal?: { code: string | null; name: string | null } | null;
  sameUser?: boolean;
}

function ownerOf(error: unknown): { owner: Owner['lockedBy'] & { terminal?: Owner['terminal'] } | null; sameUser: boolean } {
  if (!(error instanceof ApiError)) return { owner: null, sameUser: false };
  const details = error.details as Owner | undefined;
  const lockedBy = details?.lockedBy;
  if (!lockedBy) return { owner: null, sameUser: false };
  return {
    owner: { ...lockedBy, terminal: details?.terminal ?? null },
    sameUser: Boolean(details?.sameUser)
  };
}

/** Suelta sin esperar respuesta: sirve para `pagehide`, cuando la pestaña se está yendo. */
function releaseKeepalive(resourceType: string, resourceId: string, lockToken: string): Promise<void> {
  return postKeepalive('/edit-locks/release', { resourceType, resourceId, lockToken });
}

export function useEditLock(resourceType: string, resourceId: string | null, enabled = true) {
  const [state, setState] = useState<LockState>(initialLockState);
  const token = useRef<string | null>(null);
  const lastActivity = useRef<number>(0);
  const lastBeat = useRef<number>(0);
  /** Número de la petición en curso: la respuesta de una anterior se descarta y se suelta. */
  const peticion = useRef(0);
  const montado = useRef(true);
  /** Registro que la pestaña está editando ahora: sirve para decidir qué hacer con una respuesta tarde. */
  const activo = useRef<string | null>(null);
  /**
   * Suelta en vuelo. El servidor conserva el token de la misma pestaña, así que un release que
   * llegue **después** de volver a pedir el bloqueo borraría el bloqueo recién adquirido: antes de
   * adquirir se espera a que termine la suelta anterior.
   */
  const sueltaEnVuelo = useRef<Promise<unknown>>(Promise.resolve());

  const soltar = useCallback(async () => {
    const actual = token.current;
    token.current = null;
    if (!actual || !resourceId) return;
    sueltaEnVuelo.current = api('/edit-locks/release', {
      method: 'POST',
      body: { resourceType, resourceId, lockToken: actual }
    }).catch(() => {
      // Si falla, el bloqueo caduca solo: no hay nada que hacer y no se molesta al usuario.
    });
    await sueltaEnVuelo.current;
  }, [resourceType, resourceId]);

  const pedir = useCallback(async (takeover = false) => {
    if (!resourceId) return;
    const recurso = resourceId;
    // Ni un `retry` ni un `pageshow` deben adquirir un bloqueo para una pantalla que ya no edita.
    if (activo.current !== recurso) return;
    const id = ++peticion.current;
    // Y nunca antes de que aterrice la suelta anterior del mismo registro, pero con tope: si esa
    // petición se queda colgada (red mala, pestaña restaurada), la pantalla no puede quedarse
    // esperando para siempre.
    await Promise.race([
      sueltaEnVuelo.current.catch(() => undefined),
      new Promise((listo) => window.setTimeout(listo, 3000))
    ]);
    // Mientras se esperaba, el diálogo puede haberse cerrado o cambiado de registro.
    if (id !== peticion.current || activo.current !== recurso || !montado.current) return;
    setState(acquiring());
    try {
      const response = await api<{ lockToken: string; expiresAt: string }>('/edit-locks', {
        method: 'POST',
        body: { resourceType, resourceId, holderId: editHolder(), takeover }
      });
      if (!montado.current || id !== peticion.current) {
        // Llegó tarde. Si la pestaña ya no edita ese registro (diálogo cerrado, o se cambió a otro)
        // hay que soltarlo, o quedaría bloqueado quince minutos sin dueño. Si sigue en el mismo
        // registro —StrictMode, que monta dos veces— no se suelta: el token es el mismo y borrarlo
        // anularía el bloqueo vigente.
        if (shouldReleaseLate(recurso, activo.current)) {
          sueltaEnVuelo.current = releaseKeepalive(resourceType, recurso, response.lockToken);
        }
        return;
      }
      token.current = response.lockToken;
      lastActivity.current = performance.now();
      lastBeat.current = performance.now();
      setState(held(response.expiresAt));
    } catch (error) {
      if (!montado.current || id !== peticion.current) return;
      if (error instanceof ApiError && error.code === 'RESOURCE_LOCKED') {
        const { owner, sameUser } = ownerOf(error);
        setState(owner
          ? locked({ fullName: owner.fullName, terminal: owner.terminal }, sameUser)
          : failed(error.message));
        return;
      }
      setState(failed(error instanceof Error ? error.message : 'No se ha podido comprobar el bloqueo'));
    }
  }, [resourceType, resourceId]);

  useEffect(() => {
    montado.current = true;
    if (!enabled || !resourceId) {
      setState(initialLockState);
      return () => { montado.current = false; activo.current = null; };
    }
    activo.current = resourceId;
    void pedir();
    return () => {
      montado.current = false;
      peticion.current += 1;
      activo.current = null;
      void soltar();
    };
  }, [enabled, resourceId, pedir, soltar]);

  // Actividad: teclas, clics y escritura mientras se edita. Sin esto no se late.
  useEffect(() => {
    if (state.status !== 'held') return;
    // Reloj monótono: si el del sistema salta atrás (ajuste NTP), no se dejan de mandar latidos.
    const marcar = () => { lastActivity.current = performance.now(); };
    const eventos: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'input'];
    // En captura: algún componente (Radix) corta la propagación de `keydown` y `pointerdown`.
    for (const evento of eventos) window.addEventListener(evento, marcar, { passive: true, capture: true });
    return () => {
      for (const evento of eventos) window.removeEventListener(evento, marcar, { capture: true });
    };
  }, [state.status]);

  // Latido: solo si ha habido actividad desde el anterior.
  useEffect(() => {
    if (state.status !== 'held') return;
    const id = window.setInterval(async () => {
      const actual = token.current;
      if (!actual || !resourceId) return;
      if (!shouldBeat(lastActivity.current, lastBeat.current)) return;
      const enviadoEn = performance.now();
      try {
        const response = await api<{ expiresAt: string }>('/edit-locks/heartbeat', {
          method: 'POST',
          body: { resourceType, resourceId, lockToken: actual, holderId: editHolder() }
        });
        // Se apunta el momento del envío: la actividad ocurrida mientras la petición viajaba no
        // puede quedar por debajo y perderse.
        // Si mientras viajaba el latido se soltó o se readquirió, su respuesta ya no manda.
        if (token.current !== actual) return;
        lastBeat.current = enviadoEn;
        if (montado.current) setState(held(response.expiresAt));
      } catch (error) {
        if (!montado.current || token.current !== actual) return;
        if (error instanceof ApiError && error.code === 'EDIT_LOCK_LOST') {
          token.current = null;
          const { owner } = ownerOf(error);
          setState(lost(owner ? { fullName: owner.fullName, terminal: owner.terminal } : null));
          return;
        }
        // Fallo pasajero (red, 500): se conserva el token y se reintenta en el siguiente latido.
      }
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [state.status, resourceType, resourceId]);

  // Cerrar la pestaña o recargar no ejecuta la limpieza de React: se suelta a mano.
  useEffect(() => {
    const alSalir = () => {
      const actual = token.current;
      token.current = null;
      if (actual && resourceId) sueltaEnVuelo.current = releaseKeepalive(resourceType, resourceId, actual);
    };
    window.addEventListener('pagehide', alSalir);
    // Si el navegador restaura la página desde la caché, el bloqueo se soltó al salir: se vuelve a
    // pedir en vez de dejar la pantalla editable con un token que ya no existe.
    const alVolver = (event: PageTransitionEvent) => { if (event.persisted) void pedir(); };
    window.addEventListener('pageshow', alVolver);
    return () => {
      window.removeEventListener('pagehide', alSalir);
      window.removeEventListener('pageshow', alVolver);
    };
  }, [resourceType, resourceId, pedir]);

  // ── Aviso en vivo ────────────────────────────────────────────────────────────
  // Sin esto, quien está en solo lectura no se entera de que el registro se ha liberado hasta
  // cerrar y reabrir, y la pestaña que pierde el bloqueo (por «continuar aquí» en otra pestaña o
  // por una liberación forzada desde Administración) tarda hasta un latido en enterarse.
  const recursoEvento = resourceId ? `${resourceType}:${resourceId}` : null;
  const estadoRef = useRef(state);
  estadoRef.current = state;

  useEventStream(Boolean(enabled && recursoEvento), (event) => {
    if (!recursoEvento || event.id !== recursoEvento) return;
    const decision = decideEvent(
      event.type, event.data as Record<string, unknown> | undefined, estadoRef.current, editHolder()
    );
    if (decision.action === 'retry') {
      void pedir();
      return;
    }
    if (decision.action === 'free') {
      setState(free());
      return;
    }
    if (decision.action === 'lost') {
      // Se pasa a solo lectura **ya**, sin esperar al latido: no se sigue tecleando en un
      // formulario que va a fallar al guardar.
      token.current = null;
      peticion.current += 1;
      setState(lost(decision.owner));
      return;
    }
    if (decision.action === 'owner') setState(locked(decision.owner, false));
  });

  return {
    state,
    canEdit: canEdit(state),
    takeOver: () => pedir(true),
    retry: () => pedir(false),
    release: soltar
  };
}
